import type { DeviceStatusResponse } from '@/types/api-client/device-tools'
import { isCriticalSeverity, isHealthySeverity, isWarningSeverity } from './severityStatuses'

/**
 * Checks if a UISP device is offline based on nested status fields.
 * UISP returns deviceOnline: true even when the device is disconnected,
 * so we must check fullDetails.identification.site.status or fullDetails.overview.status
 */
export const isUispOffline = (result: DeviceStatusResponse): boolean => {
	const siteStatus = result.fullDetails?.identification?.site?.status
	const overviewStatus = result.fullDetails?.overview?.status
	return siteStatus === 'disconnected' || overviewStatus === 'disconnected'
}

/**
 * Checks if a UISP device is online based on nested status fields.
 * Used to recognize healthy state when severity is "n/a"
 */
export const isUispOnline = (result: DeviceStatusResponse): boolean => {
	const siteStatus = result.fullDetails?.identification?.site?.status
	const overviewStatus = result.fullDetails?.overview?.status

	// Check nested status in fullDetails (preferred)
	if (siteStatus === 'active' || overviewStatus === 'active') {
		return true
	}

	// Fallback: use deviceOnline when fullDetails is null/empty
	// This handles UISP responses where fullDetails is not populated
	// Only apply fallback for UISP devices (identified by severity "n/a" or brand "Uisp")
	const isUispDevice = result.severity === 'n/a' || result.deviceBrand?.toLowerCase() === 'uisp'
	if (isUispDevice && !result.fullDetails && result.deviceOnline === true) {
		return true
	}

	return false
}

/**
 * Device health status breakdown
 */
export interface DeviceHealthStatus {
	/** Device is operating normally with no issues */
	isHealthy: boolean
	/** Device has minor issues that should be monitored */
	hasWarning: boolean
	/** Device has critical issues requiring immediate attention */
	hasCritical: boolean
}

/**
 * Analyzes device status response and returns health indicators
 *
 * @param result - The device status response from the API
 * @returns Object containing health status flags
 *
 * @example
 * const health = getHealthStatus(statusResponse)
 * if (health.isHealthy) {
 *   // Show green status
 * } else if (health.hasWarning) {
 *   // Show yellow status
 * } else if (health.hasCritical) {
 *   // Show red status
 * }
 */
export const getHealthStatus = (result: DeviceStatusResponse | null | undefined): DeviceHealthStatus => {
	if (!result) {
		return {
			isHealthy: false,
			hasWarning: false,
			hasCritical: false
		}
	}

	// Check for critical conditions first
	// Note: UISP returns deviceOnline: true even when disconnected, so check nested status
	const hasCritical =
		result.deviceOnline === false ||
		isUispOffline(result) ||
		isCriticalSeverity(result.severity)

	// Check for warning conditions
	const hasWarning = !hasCritical && isWarningSeverity(result.severity)

	// Device is healthy if no alarms, online, and severity indicates normal operation
	// Note: UISP uses severity "n/a" so we also check nested status for "active"
	const isHealthy =
		!hasCritical &&
		!hasWarning &&
		(isHealthySeverity(result.severity) || isUispOnline(result)) &&
		!result.alarmStatus &&
		result.deviceOnline !== false

	return {
		isHealthy,
		hasWarning,
		hasCritical
	}
}
